import * as queryString from 'qs'

import { authService } from '@oneblink/apps-react'

export async function generateHeaders(addAuth?: boolean) {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  }

  if (addAuth) {
    // Check auth service for a token if user is logged in
    const idToken = await authService.getIdToken()
    if (idToken) {
      headers.Authorization = `Bearer ${idToken}`
    }
  }

  return headers
}

export class HTTPError extends Error {
  status: number

  constructor(statusCode: number, message: string) {
    super(message)
    this.status = statusCode
  }
}

async function fetchJSON<T>(url: string, options?: RequestInit): Promise<T> {
  let response

  try {
    response = await fetch(url, options)
  } catch (e) {
    console.error(e)
    throw new HTTPError(666, 'Could not contact server')
  }

  if (response.status === 204) {
    // @ts-expect-error void is ok here
    return
  }

  try {
    const body = await response.json()

    if (response.ok) return body

    throw new HTTPError(response.status, body.message)
  } catch (e) {
    console.error(e)
    // @ts-expect-error void is ok here
    if (response.ok) return

    throw e
  }
}

export async function searchRequest<T>({
  url,
  headers,
  searchParameters,
  abortSignal,
  addAuthHeaders = false,
}: {
  url: string
  headers?: Record<string, string>
  searchParameters: { [key: string]: unknown }
  abortSignal?: AbortSignal
  addAuthHeaders?: boolean
}): Promise<T> {
  const queryStringParams = queryString.stringify(searchParameters || {})
  const body = await getRequest<T>({
    url: `${url}?${queryStringParams}`,
    headers,
    abortSignal,
    addAuthHeaders,
  })
  return body
}

export async function getRequest<T>({
  url,
  headers,
  abortSignal,
  addAuthHeaders,
}: {
  url: string
  headers?: Record<string, string>
  abortSignal?: AbortSignal
  addAuthHeaders?: boolean
}): Promise<T> {
  const options = {
    method: 'GET',
    headers: headers ? headers : await generateHeaders(addAuthHeaders),
    signal: abortSignal,
  }

  return fetchJSON<T>(url, options)
}

export async function postRequest<OutT>({
  url,
  resource,
  abortSignal,
  addAuthHeaders = false,
}: {
  url: string
  resource?: unknown
  abortSignal?: AbortSignal
  addAuthHeaders?: boolean
}): Promise<OutT> {
  const opts = {
    method: 'POST',
    headers: await generateHeaders(addAuthHeaders),
    body: JSON.stringify(resource),
    signal: abortSignal,
  }

  return fetchJSON<OutT>(url, opts)
}

export async function putRequest<OutT>({
  url,
  resource,
  abortSignal,
  addAuthHeaders = false,
}: {
  url: string
  resource: unknown
  abortSignal?: AbortSignal
  addAuthHeaders?: boolean
}): Promise<OutT> {
  const opts = {
    method: 'PUT',
    headers: await generateHeaders(addAuthHeaders),
    body: JSON.stringify(resource),
    signal: abortSignal,
  }

  return fetchJSON<OutT>(url, opts)
}

export async function deleteRequest({
  url,
  abortSignal,
}: {
  url: string
  abortSignal?: AbortSignal
}): Promise<void> {
  const opts = {
    method: 'DELETE',
    headers: await generateHeaders(),
    signal: abortSignal,
  }

  const res = await fetch(url, opts)
  if (!res.ok) {
    const errorPayload = await res.json()
    throw new HTTPError(res.status, errorPayload.message)
  }
}
